import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { PanelDefinition } from '@/core';
import { getExtractVariableLabelsFunction, InitializedDataQueryPlugin } from '@/plugin-system';
import type { PanelMetadata } from './list-panels';

export interface UsePanelLabelExtractionParams {
  open: boolean;
  isLoadingPlugins: boolean;
  panelsMetadata: PanelMetadata[];
  panels: Record<string, PanelDefinition>;
  variableName: string;
  initializedPlugins: Map<string, InitializedDataQueryPlugin>;
}

export interface UsePanelLabelExtractionResult {
  panelLabelNames: Map<string, string[]>;
  isLoading: boolean;
}

/**
 * Extracts label names from panels that use a specific variable.
 */
async function extractPanelLabels(
  panelsMetadata: PanelMetadata[],
  panels: Record<string, PanelDefinition>,
  variableName: string,
  initializedPlugins: Map<string, InitializedDataQueryPlugin>
) {
  const labelNamesMap = new Map<string, string[]>();

  for (const panel of panelsMetadata) {
    const alreadyUsesVariable = panel.variableUsage?.includes(variableName) ?? false;
    if (!alreadyUsesVariable) continue;

    const panelDef = panels[panel.panelKey];
    if (!panelDef) continue;

    const labelNamesSet = new Set<string>();

    // Check each query in the panel
    for (const queryDef of panelDef.spec.queries ?? []) {
      const pluginKind = queryDef.spec?.plugin?.kind;
      if (!pluginKind) continue;

      const plugin = initializedPlugins.get(pluginKind);
      if (!plugin) continue;

      const extractFn = getExtractVariableLabelsFunction(plugin);
      if (!extractFn) continue;

      try {
        const result = await extractFn.call(plugin, queryDef.spec.plugin.spec, variableName);
        result.labelNames.forEach((label) => labelNamesSet.add(label));
      } catch {
        // Plugin doesn't support extraction for this query, skip silently
      }
    }

    if (labelNamesSet.size > 0) {
      labelNamesMap.set(panel.panelKey, Array.from(labelNamesSet));
    }
  }

  return labelNamesMap;
}

/**
 * Extract label names for panels that use a specific variable.
 * Uses plugin's extractVariableLabels method (can access query grammar AST).
 */
export function usePanelLabelExtraction({
  open,
  isLoadingPlugins,
  panelsMetadata,
  panels,
  variableName,
  initializedPlugins,
}: UsePanelLabelExtractionParams): UsePanelLabelExtractionResult {
  // Count panels using the variable for cache key
  const panelCount = useMemo(
    () => panelsMetadata.filter((p) => p.variableUsage?.includes(variableName)).length,
    [panelsMetadata, variableName]
  );

  // Include plugin count in key to re-run when plugins finish loading
  const pluginCount = initializedPlugins.size;

  const { data, isLoading } = useQuery({
    queryKey: ['panel-label-extraction', variableName, panelCount, pluginCount],
    queryFn: () => extractPanelLabels(panelsMetadata, panels, variableName, initializedPlugins),
    enabled: open && !isLoadingPlugins && pluginCount > 0,
  });

  return {
    panelLabelNames: data ?? new Map(),
    isLoading,
  };
}
